import type { OnsitePrice } from '@/types';

// currentPageItem derives the entitlement item key for an ItemSource=page price
// from the current page: the canonical link's path when present, else
// window.location.pathname. The trailing slash is trimmed (except root "/"),
// and the query string and hash are dropped (pathname only). Case is preserved
// (paths can be case-sensitive) and the origin/host is excluded (DomainID
// already scopes entitlements). The widget uses this same value for both the
// checkout item and self-suppression, so the publisher never computes it.
export function currentPageItem(): string {
  const canonical = document
    .querySelector<HTMLLinkElement>('link[rel="canonical"]')
    ?.getAttribute('href');
  // A relative reference resolves against the page URL, so the result is always
  // a same-origin path even when the canonical href is relative or odd.
  let path = window.location.pathname;
  if (canonical) {
    path = new URL(canonical, window.location.href).pathname;
  }
  if (path.length > 1 && path.endsWith('/')) {
    path = path.slice(0, -1);
  }
  return path;
}

// isItemScoped reports whether a price grants a single item rather than the
// whole product (GrantScope=item).
export function isItemScoped(price: OnsitePrice): boolean {
  return price.grantScope === 'item';
}

// resolveItemForPrice returns the item a paywall checkout should carry for the
// given price, or undefined when the price is not item-scoped. A page-sourced
// item is taken from the current page (a publisher-passed item is ignored — the
// page is authoritative); a runtime-sourced item is the value supplied to
// showPaywall.
export function resolveItemForPrice(price: OnsitePrice, suppliedItem?: string): string | undefined {
  if (!isItemScoped(price)) {
    return undefined;
  }
  if (price.itemSource === 'page') {
    return currentPageItem() || undefined;
  }
  return suppliedItem?.trim() || undefined;
}

// isPaywallPriceVisible decides whether a price should render in a product
// paywall. Product-scoped prices (and recurring/donation) always show; an
// item-scoped price shows only when its item can be resolved — a page-sourced
// price self-sources from the URL, while a runtime-sourced price needs an item
// supplied to showPaywall({ product, item }).
export function isPaywallPriceVisible(price: OnsitePrice, suppliedItem?: string): boolean {
  if (!isItemScoped(price)) {
    return true;
  }
  return !!resolveItemForPrice(price, suppliedItem);
}
